// Browser-safe environment validation for access-control-admin
// Intentionally avoids direct use of Node's process in the browser.

type Env = Record<string, string | boolean | undefined>;

function readEnv(): Env {
  try {
    const rt = (globalThis as unknown as { __ENV__?: Record<string, string | undefined> }).__ENV__;
    const meta = (globalThis as unknown as { importMetaEnv?: Record<string, string | undefined> })
      .importMetaEnv;
    const vite = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
    const node = (
      globalThis as unknown as { process?: { env?: Record<string, string | undefined> } }
    ).process?.env;
    return { ...(meta ?? {}), ...(vite ?? {}), ...(node ?? {}), ...(rt ?? {}) } as Env;
  } catch {
    return {};
  }
}

export function validateEnv(): void {
  const env = readEnv();
  const warnings: string[] = [];

  const rawIdp = env['VITE_IDP_BASE_URL'];
  const idpBase = String(rawIdp ?? '').trim();
  const acBase = String(env['VITE_ACCESS_CONTROL_BASE_URL'] ?? '').trim();
  const audience = String(env['VITE_IDP_AUDIENCE'] ?? '').trim();
  const gaId = String(env['VITE_GA_ID'] ?? '').trim();
  const fbPixel = String(env['VITE_FB_PIXEL'] ?? '').trim();
  const offline =
    String(env['VITE_CI_OFFLINE'] ?? '').toLowerCase() === 'true' ||
    (typeof process !== 'undefined' && process.env?.['CI_OFFLINE'] === 'true');

  // IDP base is strongly recommended for admin flows and hosted pages
  if (!idpBase) {
    // Empty string explicitly means same-origin (CloudFront auth proxy).
    if (rawIdp !== '') {
      warnings.push(
        'VITE_IDP_BASE_URL is not set. OAuth flows use defaults and forgot/reset redirects will be disabled. Add VITE_IDP_BASE_URL to .env.local.',
      );
    }
  } else if (!/^https?:\/\//i.test(idpBase) && idpBase !== '/') {
    warnings.push('VITE_IDP_BASE_URL must include http(s) scheme.');
  }

  // API base: if missing, clients may fall back to a production URL. Encourage explicit local value.
  // "/" or empty string means same-origin (CloudFront API proxy).
  const rawAcBase = env['VITE_ACCESS_CONTROL_BASE_URL'];
  if (!acBase) {
    // Empty string explicitly means same-origin (CloudFront API proxy).
    if (rawAcBase !== '' && rawAcBase !== '/') {
      try {
        const sameOrigin = window.location.origin;
        warnings.push(
          `VITE_ACCESS_CONTROL_BASE_URL is not set. Some clients default to a production URL. For local dev, set it to ${sameOrigin}.`,
        );
      } catch {
        warnings.push(
          'VITE_ACCESS_CONTROL_BASE_URL is not set. Some clients default to a production URL. Set an explicit base URL.',
        );
      }
    }
  } else if (acBase === '/api' || acBase === '/api/') {
    warnings.push(
      'VITE_ACCESS_CONTROL_BASE_URL looks like "/api". Access Control API clients already include "/api" in generated paths; use "/" or a non-API base URL.',
    );
  } else if (!/^https?:\/\//i.test(acBase) && acBase !== '/') {
    warnings.push('VITE_ACCESS_CONTROL_BASE_URL must include http(s) scheme.');
  }

  // Audience is configurable via meta/env; default is 'control-admin'.
  // Warn only if it looks suspicious (e.g., empty whitespace or oddly short tokens)
  if (audience && audience.length < 3) {
    warnings.push('VITE_IDP_AUDIENCE looks unusually short.');
  }

  // Analytics tags are optional and consent-gated
  if (gaId && !/^G-/.test(gaId)) {
    warnings.push('VITE_GA_ID looks unusual (expected to start with "G-").');
  }
  if (fbPixel && !/^\d{5,}$/.test(fbPixel)) {
    warnings.push('VITE_FB_PIXEL should be a numeric pixel id.');
  }

  if (offline) {
    // Informative only
    warnings.push('CI_OFFLINE is active; websockets/polling will be disabled where supported.');
  }

  if (warnings.length) {
    try {
      console.groupCollapsed('%c[access-control-admin] Env validation', 'color:#6aae43');
      for (const w of warnings) {
        console.warn('•', w);
      }
      console.info(
        'Tip: create apps/access-control/access-control-admin/.env.local with VITE_IDP_BASE_URL and VITE_ACCESS_CONTROL_BASE_URL.',
      );
      console.groupEnd();
    } catch {
      // noop
    }
  }
}
